import { motion } from 'framer-motion';
import { Target, ArrowRight, Check } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { generateJourney } from '../../../services/journeyAlgorithmService';

import type { DashboardPage } from './Layout';

interface JourneySetupPageProps {
  onNavigate: (page: DashboardPage) => void;
  onDone?: () => void;
}

type Step = 'form' | 'generating' | 'success';

export default function JourneySetupPage({ onNavigate, onDone }: JourneySetupPageProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('form');
  const [targetRole, setTargetRole] = useState('');
  const [intensity, setIntensity] = useState<'light' | 'moderate' | 'all-in'>('moderate');
  const [targetDate, setTargetDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setStep('generating');
    try {
      await generateJourney({
        target_role: targetRole.trim() || null,
        target_date: targetDate || null,
        job_hunt_intensity: intensity,
      });
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.overview.journeySetup.errorDefault'));
      setStep('form');
    }
  };

  const goToDashboard = () => {
    onDone?.();
    onNavigate('dashboard');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-lg mx-auto p-4 sm:p-6"
    >
      <div className="rounded-xl border vscode-border vscode-bg-secondary overflow-hidden shadow-sm">
        <div
          className="shrink-0 px-4 sm:px-6 py-3 flex items-center gap-2 border-b"
          style={{ backgroundColor: 'var(--vscode-primary)', borderColor: 'var(--vscode-primary)' }}
        >
          <Target className="w-5 h-5 shrink-0 text-[var(--vscode-primary-text,#fff)]" />
          <h1 className="text-lg sm:text-xl font-bold text-[var(--vscode-primary-text,#fff)]">
            {t('dashboard.overview.journeySetup.title')}
          </h1>
        </div>
        <div className="p-6 sm:p-8">
          {step === 'form' && (
            <>
              <p className="text-sm vscode-text-tertiary mb-5">{t('dashboard.overview.journeySetup.help')}</p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium vscode-text-secondary mb-1.5">{t('dashboard.overview.journeySetup.targetRoleLabel')}</label>
                  <input
                    type="text"
                    value={targetRole}
                    onChange={(e) => setTargetRole(e.target.value)}
                    placeholder={t('dashboard.overview.journeySetup.targetRolePlaceholder')}
                    className="w-full px-4 py-3 min-h-[44px] rounded-lg border vscode-border vscode-bg-tertiary vscode-text-primary placeholder:vscode-text-tertiary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium vscode-text-secondary mb-1.5">{t('dashboard.overview.journeySetup.timeLabel')}</label>
                  <p className="text-xs vscode-text-tertiary mb-1.5">{t('dashboard.overview.journeySetup.timeHelp')}</p>
                  <select
                    value={intensity}
                    onChange={(e) => setIntensity(e.target.value as 'light' | 'moderate' | 'all-in')}
                    className="w-full px-4 py-3 min-h-[44px] rounded-lg border vscode-border vscode-bg-tertiary vscode-text-primary"
                  >
                    <option value="light">{t('dashboard.overview.journeySetup.timeLight')}</option>
                    <option value="moderate">{t('dashboard.overview.journeySetup.timeModerate')}</option>
                    <option value="all-in">{t('dashboard.overview.journeySetup.timeAllIn')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium vscode-text-secondary mb-1.5">{t('dashboard.overview.journeySetup.targetDateLabel')}</label>
                  <input
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className="w-full px-4 py-3 min-h-[44px] rounded-lg border vscode-border vscode-bg-tertiary vscode-text-primary"
                  />
                  <p className="text-xs vscode-text-tertiary mt-1">{t('dashboard.overview.journeySetup.targetDatePlaceholder')}</p>
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 min-h-[44px] py-3.5 rounded-lg bg-[var(--vscode-primary)] text-[var(--vscode-primary-text,#fff)] font-medium hover:opacity-90 transition-opacity touch-manipulation"
                >
                  {t('dashboard.overview.journeySetup.createButton')}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </>
          )}

          {step === 'generating' && (
            <div className="py-12 flex flex-col items-center justify-center gap-4">
              <LoadingSpinner size="lg" showText={false} />
              <p className="vscode-text-primary font-medium">{t('dashboard.overview.journeySetup.creating')}</p>
              <p className="text-sm vscode-text-tertiary">{t('dashboard.overview.journeySetup.creatingSub')}</p>
            </div>
          )}

          {step === 'success' && (
            <div className="py-6 text-center">
              <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-lg font-semibold vscode-text-primary mb-2">{t('dashboard.overview.journeySetup.successTitle')}</h2>
              <p className="text-sm vscode-text-tertiary mb-6">
                {t('dashboard.overview.journeySetup.successBody')}
              </p>
              <button
                type="button"
                onClick={goToDashboard}
                className="w-full flex items-center justify-center gap-2 min-h-[44px] py-3.5 rounded-lg font-semibold text-white hover:opacity-90 transition-opacity touch-manipulation"
                style={{ background: 'var(--brand-cta-bg)' }}
              >
                {t('dashboard.overview.journeySetup.goToDashboard')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
      <p className="text-center text-sm vscode-text-tertiary mt-4">
        {t('dashboard.overview.journeySetup.changeHint')}
      </p>
    </motion.div>
  );
}
